// Pure helpers for the Predictor's MatchdayWindow.
//
//   buildSlots(matches)         → array of { key, label, matches } in fixed order
//   pickCurrentSlot(slots, now) → the slot to default-show on mount
//
// ESM named exports — module is imported from JSX bundled by Rollup.
// Kept React-free so it can be unit-tested under plain Vitest without jsdom.

export const SLOT_ORDER = ['MD1', 'MD2', 'MD3', 'R32', 'R16', 'QF', 'SF', 'F'];

export const SLOT_LABEL = {
  MD1: 'MATCHDAY 1', MD2: 'MATCHDAY 2', MD3: 'MATCHDAY 3',
  R32: 'ROUND OF 32', R16: 'ROUND OF 16',
  QF:  'QUARTER-FINALS', SF: 'SEMI-FINALS', F: 'FINAL',
};

const LOOKAHEAD_MS = 24 * 3600 * 1000;

function slotKeyFor(match) {
  if (match.type === 'group') {
    return `MD${match.matchday}`;
  }
  // knockout — uses .group field by existing convention ('R32', 'QF', etc.)
  return (match.group || '').toUpperCase();
}

export function buildSlots(matches) {
  const buckets = Object.fromEntries(SLOT_ORDER.map(k => [k, []]));
  for (const m of matches || []) {
    const k = slotKeyFor(m);
    if (buckets[k]) buckets[k].push(m);
  }
  return SLOT_ORDER.map(k => ({ key: k, label: SLOT_LABEL[k], matches: buckets[k] }));
}

export function pickCurrentSlot(slots, nowMs) {
  const nonEmpty = slots.filter(s => s.matches.length > 0);
  if (nonEmpty.length === 0) return slots[0];

  const unfinished = nonEmpty.filter(s =>
    !s.matches.every(m => m.status === 'completed')
  );

  // Tournament fully complete → stick on the last slot that has matches.
  if (unfinished.length === 0) {
    return nonEmpty[nonEmpty.length - 1];
  }

  // Prefer the first unfinished slot whose earliest kickoff is "near enough."
  for (const s of unfinished) {
    const kickoffs = s.matches
      .map(m => (m.kickoff_at ? Date.parse(m.kickoff_at) : null))
      .filter(t => t !== null && Number.isFinite(t));

    const inWindow =
      kickoffs.length === 0 ||
      Math.min(...kickoffs) <= nowMs + LOOKAHEAD_MS;

    if (inWindow) return s;
  }

  // No unfinished slot is within the lookahead window — show the next one.
  return unfinished[0];
}
